import { useState } from 'react';
import { Trans } from '../i18n/Trans';
import { ApiHelper } from '../helper/ApiHelper';
import { Modal } from './Modal';

export interface OrderContact {
  name: string;
  phone: string;
  email: string;
  company: string;
  address: string;
  /** 要不要开票；不勾选时下面两项一律不发（后端也会再清一次） */
  needInvoice: boolean;
  /** 发票类型编码：'special' 专票 / 'plain' 普票；中文名只在 trans.json 里 */
  invoiceType: string;
  taxNo: string;
}

/** 发票类型：**只存编码**，显示名走字典。顺序即界面顺序。 */
const INVOICE_TYPES = ['special', 'plain'];

/**
 * 下单弹窗。**收货地址、联系电话、公司名是发货与开票必需**，所以下单不能一键静默提交，
 * 必须在这里把人、地址、公司要清楚；姓名/邮箱能从账号带就带，别让用户重复输。
 * 开票信息做成勾选后展开：不开票的人不该被税号这种字段拦住。
 * 提交只发 historyId + 这张表单，价格由后端从库里取（前端那份不可信）。
 */
export function OrderDialog({
  historyId, claimToken, total, currency, lines, priced, defaults, onDone, onClose,
}: {
  historyId?: number;
  claimToken?: string;
  total: number;
  currency: string;
  lines: number;
  priced: number;
  defaults: Partial<OrderContact>;
  onDone: () => void;
  onClose: () => void;
}) {
  const [c, setC] = useState<OrderContact>({
    name: defaults.name ?? '',
    phone: defaults.phone ?? '',
    email: defaults.email ?? '',
    company: defaults.company ?? '',
    address: defaults.address ?? '',
    // 发票类型不给默认值：专票要一般纳税人资格，替客户默认选一个开错了要作废重开
    needInvoice: false,
    invoiceType: '',
    taxNo: '',
  });
  const [remark, setRemark] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const set = (k: keyof OrderContact) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setC({ ...c, [k]: e.target.value });

  // 取消勾选就把开票那两项清掉：留着上次填的税号，用户以为没开票、后台却收到开票信息
  const toggleInvoice = (on: boolean) =>
    setC({ ...c, needInvoice: on, invoiceType: on ? c.invoiceType : '', taxNo: on ? c.taxNo : '' });

  // 缺什么就说缺什么：按钮不做"灰着但不说为什么"
  const missing = [
    !c.name.trim() && Trans.t('order.f.name'),
    !c.phone.trim() && Trans.t('order.f.phone'),
    !c.address.trim() && Trans.t('order.f.address'),
    !c.company.trim() && Trans.t('order.f.company'),
    c.needInvoice && !c.invoiceType && Trans.t('order.f.invoiceType'),
    c.needInvoice && !c.taxNo.trim() && Trans.t('order.f.taxNo'),
  ].filter(Boolean) as string[];

  const submit = async () => {
    setBusy(true);
    setErr('');
    try {
      // 免登录也能下单：带上这次估价的 claimToken 证明"这张单是我刚跑的"
      await ApiHelper.call('/api/orders', 'POST', { historyId, claimToken, contact: c, remark });
      onDone();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={Trans.t('order.dialogTitle')} onClose={onClose} closeOnBackdrop={false}>
      <div className="order-sum">
        <span>{Trans.t('quote.sumPriced')} <b>{priced}/{lines}</b></span>
        <span>{Trans.t('quote.sumTotal')} <b>{total.toFixed(2)} {currency}</b></span>
      </div>

      <label className="login-label">{Trans.t('order.f.name')} *</label>
      <input value={c.name} onChange={set('name')} placeholder={Trans.t('order.p.name')} />

      <label className="login-label">{Trans.t('order.f.phone')} *</label>
      <input value={c.phone} onChange={set('phone')} placeholder={Trans.t('order.p.phone')} />

      <label className="login-label">{Trans.t('order.f.address')} *</label>
      <textarea rows={2} value={c.address} onChange={set('address')} placeholder={Trans.t('order.p.address')} />

      <label className="login-label">{Trans.t('order.f.company')} *</label>
      <input value={c.company} onChange={set('company')} placeholder={Trans.t('order.p.company')} />

      <label className="login-label">{Trans.t('order.f.email')}</label>
      <input value={c.email} onChange={set('email')} placeholder={Trans.t('order.p.email')} />

      {/* 开票信息：勾了才展开。不开票的人不该被税号这种字段挡在门口。 */}
      <label className="check-row">
        <input
          type="checkbox"
          checked={c.needInvoice}
          onChange={(e) => toggleInvoice(e.target.checked)}
        />
        <span>{Trans.t('order.f.needInvoice')}</span>
      </label>

      {c.needInvoice && (
        <div className="sub-block">
          <label className="login-label">{Trans.t('order.f.invoiceType')} *</label>
          <div className="pick-row">
            {INVOICE_TYPES.map((t) => (
              <button
                key={t}
                type="button"
                className={'btn ghost pick-one' + (c.invoiceType === t ? ' on' : '')}
                onClick={() => setC({ ...c, invoiceType: t })}
              >
                {Trans.t(`order.invoice.${t}`)}
              </button>
            ))}
          </div>
          <p className="hint">{Trans.t('order.invoiceTypeHint')}</p>

          <label className="login-label">{Trans.t('order.f.taxNo')} *</label>
          <input value={c.taxNo} onChange={set('taxNo')} placeholder={Trans.t('order.p.taxNo')} />
          <p className="hint">{Trans.t('order.invoiceTitleHint')}<b>{c.company.trim() || '—'}</b></p>
        </div>
      )}

      <label className="login-label">{Trans.t('quote.orderRemark')}</label>
      <textarea rows={2} value={remark} onChange={(e) => setRemark(e.target.value)} />

      {err && <div className="error-box">{err}</div>}

      <button className="btn accent login-btn" disabled={busy || missing.length > 0} onClick={submit}>
        {busy ? Trans.t('quote.ordering') : Trans.t('order.submit')}
      </button>
      {missing.length > 0 && (
        <p className="hint">{Trans.t('order.needFields')}{missing.join('、')}</p>
      )}
    </Modal>
  );
}
